// @flow

import * as React from 'react';

import {AlignBox} from 'sail/Align';
import Box from 'sail/Box';
import {Body, BodyAlt, BodyTextProps} from 'sail/Text';
import Icon from 'sail/Icon';

import CheckerError from './CheckerError';
import CheckerMessage from './CheckerMessage';

const CheckCircleIcon = (props) => (
  <Icon icon="checkCircle" color={props.color || 'green'} size={12} />
);

const CancelCircleIcon = (props) => (
  <Icon icon="cancelCircle" color={props.color || 'red'} size={12} />
);

type IntegrationCheckProps = {
  title: React.Element<typeof CheckerMessage>,
  success: ?boolean,
  errorMessage: ?string,
  errorMessageCode: ?string,
  event: ?string,
  log: ?string,
  dashboardUrl: string,
};

/**
 * Component used for each test case in a scenario
 */
const IntegrationCheck = (props: IntegrationCheckProps) => {
  const {dashboardUrl, errorMessageCode, errorMessage, event, log} = props;
  let icon = null;

  if (props.success === true) {
    icon = <CheckCircleIcon icon="checkCircle" color="green" size={12} />;
  } else if (props.success === false) {
    icon = <CancelCircleIcon color="red" />;
  } else {
    icon = <CheckCircleIcon icon="checkCircle" color="disabled" size={12} />;
  }
  return (
    <AlignBox reference={BodyTextProps}>
      <Box padding={{right: 8}}>{icon}</Box>
      <BodyAlt>
        <Box>{props.title}</Box>
        {'   '}
        <Body display="inline" color="gray">
          <CheckerError
            errorMessage={errorMessage}
            errorMessageCode={errorMessageCode}
            event={event}
            log={log}
            dashboardUrl={dashboardUrl}
          />
        </Body>
      </BodyAlt>
    </AlignBox>
  );
};

type IntegrationCheckSummaryProps = {
  totalCount: number,
  successCount: number,
  incompleteCount: number,
};

/**
 * Component used to show summary of results when collapsed
 */
const IntegrationCheckSummary = (props: IntegrationCheckSummaryProps) => {
  const {totalCount, successCount, incompleteCount} = props;
  const failedCount = totalCount - incompleteCount - successCount;
  if (successCount === 0) {
    return <Body color="gray">0/{totalCount} tests completed</Body>;
  } else if (totalCount === successCount) {
    return (
      <AlignBox reference={BodyTextProps}>
        <Box padding={{right: 8}}>
          <CheckCircleIcon />
        </Box>
        <Body color="gray">All tests completed</Body>
      </AlignBox>
    );
  } else {
    return (
      <AlignBox reference={BodyTextProps}>
        {successCount > 0 ? (
          <>
            <Box padding={{right: 8}}>
              <CheckCircleIcon />
            </Box>
            <Body color="gray">
              {successCount > 1
                ? `${successCount} tests completed`
                : `${successCount} test completed`}
            </Body>
          </>
        ) : null}
        {incompleteCount > 0 ? (
          <>
            <Box padding={{horizontal: 8}}>
              <CheckCircleIcon color="disabled" />
            </Box>
            <Body color="gray">{incompleteCount} incomplete</Body>
          </>
        ) : null}
        {failedCount > 0 ? (
          <>
            <Box padding={{horizontal: 8}}>
              <CancelCircleIcon />
            </Box>
            <Body color="gray">{failedCount} failed</Body>
          </>
        ) : null}
      </AlignBox>
    );
  }
};

export default IntegrationCheck;
export {IntegrationCheckSummary};
